import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FaFileInvoiceDollar, FaPlus, FaTimes, FaSearch, FaDownload, FaCheck } from 'react-icons/fa';
import { Order } from '../../types';
import {
    TIPOS_FACTURA,
    TipoFactura,
    Comprobante,
    Receptor,
    EMISOR,
    buildComprobante,
    generateFacturaPDF,
} from '../../services/facturaService';
import {
    ComprobanteEmitido, leerComprobantes, guardarComprobantes,
} from '../../services/comprobantesStore';
import styles from './styles/Facturacion.module.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const money = (n: number) => `$${Math.round(n || 0).toLocaleString('es-AR')}`;

/**
 * Condiciones frente al IVA que ofrece el formulario.
 * La factura A solo puede emitirse a un responsable inscripto: el selector se
 * acota segun la letra elegida para que no se arme un comprobante invalido.
 */
const CONDICIONES_IVA: Record<TipoFactura, string[]> = {
    A: ['IVA Responsable Inscripto'],
    B: ['Consumidor Final', 'Responsable Monotributo', 'IVA Sujeto Exento'],
    C: ['Consumidor Final', 'Responsable Monotributo'],
};

const ORDEN_LETRAS: TipoFactura[] = ['A', 'B', 'C'];

/** Comprobante emitido tal como se persiste (sin la venta completa). */
type EmitidaRow = ComprobanteEmitido;

const loadEmitidas = leerComprobantes;

const Facturacion: React.FC = () => {
    const [emitidas, setEmitidas] = useState<EmitidaRow[]>(loadEmitidas);
    const [wizardOpen, setWizardOpen] = useState(false);
    const [toast, setToast] = useState<{ type: 'ok' | 'error'; text: string } | null>(null);

    // Las ventas se guardan para poder regenerar el PDF de un comprobante ya
    // emitido sin volver a pedirlas al backend.
    const [ordersById, setOrdersById] = useState<Record<string, Order>>({});

    const notify = useCallback((type: 'ok' | 'error', text: string) => {
        setToast({ type, text });
        window.setTimeout(() => setToast(null), 2600);
    }, []);

    const persist = useCallback((rows: EmitidaRow[]) => {
        setEmitidas(rows);
        guardarComprobantes(rows);
    }, []);

    /** El numerador es por punto de venta y arranca en 1. */
    const proximoNumero = useMemo(
        () => emitidas.reduce((max, row) => Math.max(max, row.numero), 0) + 1,
        [emitidas],
    );

    const onEmitida = useCallback(
        (comprobante: Comprobante, order: Order) => {
            persist([{ ...comprobante, emitidaEn: new Date().toISOString() }, ...emitidas]);
            setOrdersById(prev => ({ ...prev, [order.ID]: order }));
            setWizardOpen(false);
            notify('ok', `Factura ${comprobante.tipo} ${comprobante.etiqueta} emitida`);
        },
        [emitidas, persist, notify],
    );

    /** Reimpresion: solo posible si la venta origen sigue en memoria. */
    const reimprimir = async (row: EmitidaRow) => {
        let order = ordersById[row.orderId];
        if (!order) {
            try {
                const res = await fetch(`${API}/admin/orders/${row.orderId}`);
                if (res.ok) order = await res.json();
            } catch {
                /* se resuelve abajo */
            }
        }
        if (!order) {
            notify('error', 'No se pudo recuperar la venta de origen');
            return;
        }
        setOrdersById(prev => ({ ...prev, [order.ID]: order }));
        generateFacturaPDF(order, row);
    };

    const totalFacturado = useMemo(
        () => emitidas.reduce((acc, row) => acc + (row.totales?.total || 0), 0),
        [emitidas],
    );

    return (
        <div className={styles.fact}>
            <header className={styles.head}>
                <div>
                    <h2 className={styles.headTitle}>Facturación electrónica</h2>
                    <p className={styles.headSub}>
                        Emisión de comprobantes A, B y C sobre ventas ya registradas ·
                        Punto de venta {String(EMISOR.puntoVenta).padStart(4, '0')}
                    </p>
                </div>
                <button type="button" className={styles.primaryBtn} onClick={() => setWizardOpen(true)}>
                    <FaPlus /> Nueva factura
                </button>
            </header>

            <div className={styles.demoNote}>
                Entorno de demostración: los comprobantes se autorizan localmente. El CAE y su
                vencimiento son simulados y no tienen validez fiscal.
            </div>

            <div className={styles.kpis}>
                <div className={styles.kpi}>
                    <span className={styles.kpiLabel}>Comprobantes emitidos</span>
                    <span className={styles.kpiValue}>{emitidas.length}</span>
                </div>
                <div className={styles.kpi}>
                    <span className={styles.kpiLabel}>Total facturado</span>
                    <span className={styles.kpiValue}>{money(totalFacturado)}</span>
                </div>
                <div className={styles.kpi}>
                    <span className={styles.kpiLabel}>Próximo número</span>
                    <span className={styles.kpiValue}>
                        {String(EMISOR.puntoVenta).padStart(4, '0')}-{String(proximoNumero).padStart(8, '0')}
                    </span>
                </div>
            </div>

            <div className={styles.tableWrap}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th>Comprobante</th>
                            <th>Tipo</th>
                            <th>Fecha</th>
                            <th>Receptor</th>
                            <th>Venta</th>
                            <th className={styles.num}>Neto</th>
                            <th className={styles.num}>IVA</th>
                            <th className={styles.num}>Total</th>
                            <th>CAE</th>
                            <th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {emitidas.length === 0 && (
                            <tr>
                                <td colSpan={10} className={styles.empty}>
                                    <FaFileInvoiceDollar /> Todavía no se emitieron comprobantes.
                                </td>
                            </tr>
                        )}

                        {emitidas.map(row => (
                            <tr key={`${row.tipo}-${row.etiqueta}`}>
                                <td className={styles.cellId}>{row.etiqueta}</td>
                                <td>
                                    <span className={`${styles.letra} ${styles[`letra${row.tipo}`]}`}>{row.tipo}</span>
                                </td>
                                <td>{row.fecha}</td>
                                <td className={styles.ellipsis}>{row.receptor.razonSocial}</td>
                                <td className={styles.muted}>#VTA-{row.orderId}</td>
                                <td className={styles.num}>{money(row.totales.neto)}</td>
                                <td className={styles.num}>{row.totales.iva ? money(row.totales.iva) : '—'}</td>
                                <td className={`${styles.num} ${styles.strong}`}>{money(row.totales.total)}</td>
                                <td className={styles.muted}>{row.cae}</td>
                                <td>
                                    <button
                                        type="button"
                                        className={styles.iconBtn}
                                        title="Descargar PDF"
                                        aria-label={`Descargar la factura ${row.etiqueta}`}
                                        onClick={() => reimprimir(row)}
                                    >
                                        <FaDownload />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {wizardOpen && (
                <NuevaFacturaWizard
                    proximoNumero={proximoNumero}
                    onClose={() => setWizardOpen(false)}
                    onEmitida={onEmitida}
                />
            )}

            {toast && (
                <div className={`${styles.toast} ${toast.type === 'error' ? styles.toastError : ''}`}>
                    {toast.text}
                </div>
            )}
        </div>
    );
};

/* ------------------------------------------------------------------ wizard */

interface WizardProps {
    proximoNumero: number;
    onClose: () => void;
    onEmitida: (comprobante: Comprobante, order: Order) => void;
}

/**
 * Alta de comprobante en tres pasos: letra -> venta de origen -> datos del
 * receptor. Se mantiene como wizard porque la letra condiciona que campos del
 * receptor son obligatorios (la A exige CUIT).
 */
const NuevaFacturaWizard: React.FC<WizardProps> = ({ proximoNumero, onClose, onEmitida }) => {
    const [step, setStep] = useState<1 | 2 | 3>(1);
    const [tipo, setTipo] = useState<TipoFactura | null>(null);
    const [order, setOrder] = useState<Order | null>(null);

    const [orders, setOrders] = useState<Order[]>([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');

    const [razonSocial, setRazonSocial] = useState('');
    const [documento, setDocumento] = useState('');
    const [domicilio, setDomicilio] = useState('');
    const [condicionIva, setCondicionIva] = useState('');
    const [error, setError] = useState<string | null>(null);

    // Se traen las ventas mas recientes: facturar algo de hace meses es la
    // excepcion, y el buscador cubre ese caso sobre lo ya cargado.
    useEffect(() => {
        if (step !== 2) return;
        setLoading(true);
        fetch(`${API}/admin/orders?page=1&per_page=100`)
            .then(res => res.json())
            .then(data => {
                const items: Order[] = Array.isArray(data) ? data : data?.items ?? data?.data ?? [];
                setOrders(Array.isArray(items) ? items : []);
            })
            .catch(() => setOrders([]))
            .finally(() => setLoading(false));
    }, [step]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return orders;
        return orders.filter(o =>
            `${o.ID} ${o.Cliente || ''} ${o.DNI || ''}`.toLowerCase().includes(q),
        );
    }, [orders, search]);

    const elegirTipo = (letra: TipoFactura) => {
        setTipo(letra);
        setCondicionIva(CONDICIONES_IVA[letra][0]);
        setStep(2);
    };

    /** Al elegir la venta se precargan los datos del cliente que ya existen. */
    const elegirVenta = (o: Order) => {
        setOrder(o);
        setRazonSocial(o.Cliente || '');
        setDocumento((o.DNI || '').replace(/\D/g, ''));
        setDomicilio(o.Direccion || '');
        setStep(3);
    };

    const emitir = () => {
        if (!tipo || !order) return;
        const digits = documento.replace(/\D/g, '');

        if (tipo === 'A') {
            if (digits.length !== 11) {
                setError('La factura A requiere el CUIT del receptor (11 dígitos).');
                return;
            }
            if (!razonSocial.trim()) {
                setError('La factura A requiere la razón social del receptor.');
                return;
            }
        }
        // En B y C se admite consumidor final sin identificar, pero AFIP exige
        // documento cuando el importe supera el minimo vigente.
        if (tipo !== 'A' && !digits && order.Total > 344000) {
            setError('Por el importe de la operación es obligatorio identificar al receptor.');
            return;
        }

        const tipoDocumento: Receptor['tipoDocumento'] =
            digits.length === 11 ? 'CUIT' : digits.length >= 7 ? 'DNI' : 'Sin identificar';

        const receptor: Receptor = {
            razonSocial: razonSocial.trim() || 'Consumidor Final',
            documento: digits,
            tipoDocumento,
            domicilio: domicilio.trim(),
            condicionIva: condicionIva || CONDICIONES_IVA[tipo][0],
        };

        const comprobante = buildComprobante(order, tipo, proximoNumero, receptor);
        generateFacturaPDF(order, comprobante);
        onEmitida(comprobante, order);
    };

    return (
        <div
            className={styles.overlay}
            role="presentation"
            onClick={e => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div className={styles.modal} role="dialog" aria-modal="true" aria-label="Nueva factura">
                <div className={styles.modalHead}>
                    <div>
                        <h3>Nueva factura</h3>
                        <p>
                            Paso {step} de 3 ·{' '}
                            {step === 1 ? 'Tipo de comprobante' : step === 2 ? 'Venta de origen' : 'Datos del receptor'}
                        </p>
                    </div>
                    <button type="button" className={styles.iconBtn} onClick={onClose} aria-label="Cerrar">
                        <FaTimes />
                    </button>
                </div>

                <div className={styles.steps}>
                    {[1, 2, 3].map(n => (
                        <span
                            key={n}
                            className={`${styles.stepDot} ${step >= n ? styles.stepDotDone : ''}`}
                        />
                    ))}
                </div>

                <div className={styles.modalBody}>
                    {step === 1 && (
                        <div className={styles.tipoGrid}>
                            {ORDEN_LETRAS.map(letra => {
                                const def = TIPOS_FACTURA[letra];
                                return (
                                    <button
                                        key={letra}
                                        type="button"
                                        className={`${styles.tipoCard} ${tipo === letra ? styles.tipoCardActive : ''}`}
                                        onClick={() => elegirTipo(letra)}
                                    >
                                        <span className={`${styles.tipoLetra} ${styles[`letra${letra}`]}`}>{letra}</span>
                                        <strong>{def.nombre}</strong>
                                        <span className={styles.tipoDesc}>{def.destinatario}</span>
                                        <span className={styles.tipoMeta}>
                                            Cód. {def.codigo} · {def.discriminaIva ? 'IVA discriminado' : 'IVA incluido'}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    )}

                    {step === 2 && (
                        <>
                            <div className={styles.searchBar}>
                                <FaSearch />
                                <input
                                    type="search"
                                    value={search}
                                    onChange={e => setSearch(e.target.value)}
                                    placeholder="Buscar por número de venta, cliente o DNI…"
                                    aria-label="Buscar venta"
                                />
                            </div>

                            <div className={styles.pickWrap}>
                                <table className={styles.table}>
                                    <thead>
                                        <tr>
                                            <th>Venta</th>
                                            <th>Fecha</th>
                                            <th>Cliente</th>
                                            <th>Artículos</th>
                                            <th className={styles.num}>Total</th>
                                            <th />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading && (
                                            <tr><td colSpan={6} className={styles.empty}>Cargando ventas…</td></tr>
                                        )}
                                        {!loading && filtered.length === 0 && (
                                            <tr><td colSpan={6} className={styles.empty}>No hay ventas para facturar.</td></tr>
                                        )}
                                        {!loading && filtered.map(o => (
                                            <tr key={o.ID}>
                                                <td className={styles.cellId}>#VTA-{o.ID}</td>
                                                <td>{o.Fecha || '—'}</td>
                                                <td className={styles.ellipsis}>{o.Cliente || 'Consumidor Final'}</td>
                                                <td className={styles.muted}>
                                                    {(o.Items || []).reduce((a, i) => a + (Number(i.Cantidad) || 0), 0)}
                                                </td>
                                                <td className={`${styles.num} ${styles.strong}`}>{money(o.Total)}</td>
                                                <td>
                                                    <button
                                                        type="button"
                                                        className={styles.pickBtn}
                                                        onClick={() => elegirVenta(o)}
                                                    >
                                                        Seleccionar
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}

                    {step === 3 && tipo && order && (
                        <>
                            <div className={styles.resumen}>
                                <div>
                                    <span>Comprobante</span>
                                    <strong>
                                        Factura {tipo} · {String(EMISOR.puntoVenta).padStart(4, '0')}-
                                        {String(proximoNumero).padStart(8, '0')}
                                    </strong>
                                </div>
                                <div>
                                    <span>Venta de origen</span>
                                    <strong>#VTA-{order.ID}</strong>
                                </div>
                                <div>
                                    <span>Importe total</span>
                                    <strong>{money(order.Total)}</strong>
                                </div>
                            </div>

                            <div className={styles.form}>
                                <label className={styles.field}>
                                    <span>Apellido y nombre / Razón social</span>
                                    <input
                                        value={razonSocial}
                                        onChange={e => setRazonSocial(e.target.value)}
                                        placeholder={tipo === 'A' ? 'Razón social del inscripto' : 'Consumidor Final'}
                                    />
                                </label>

                                <label className={styles.field}>
                                    <span>{tipo === 'A' ? 'CUIT (11 dígitos)' : 'CUIT / DNI'}</span>
                                    <input
                                        value={documento}
                                        onChange={e => setDocumento(e.target.value)}
                                        inputMode="numeric"
                                        placeholder={tipo === 'A' ? '30712345678' : 'Opcional'}
                                    />
                                </label>

                                <label className={styles.field}>
                                    <span>Condición frente al IVA</span>
                                    <select value={condicionIva} onChange={e => setCondicionIva(e.target.value)}>
                                        {CONDICIONES_IVA[tipo].map(c => (
                                            <option key={c} value={c}>{c}</option>
                                        ))}
                                    </select>
                                </label>

                                <label className={styles.field}>
                                    <span>Domicilio</span>
                                    <input
                                        value={domicilio}
                                        onChange={e => setDomicilio(e.target.value)}
                                        placeholder="Opcional"
                                    />
                                </label>
                            </div>

                            {error && <div className={styles.error}>{error}</div>}
                        </>
                    )}
                </div>

                <div className={styles.modalFoot}>
                    {step > 1 ? (
                        <button
                            type="button"
                            className={styles.ghostBtn}
                            onClick={() => { setError(null); setStep(step === 3 ? 2 : 1); }}
                        >
                            Volver
                        </button>
                    ) : <span />}

                    {step === 3 && (
                        <button type="button" className={styles.primaryBtn} onClick={emitir}>
                            <FaCheck /> Emitir y generar PDF
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Facturacion;
